import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * RefinementChat Component
 * Dialog for refining individual questions through conversation with LLM
 */
export default function RefinementChat({
  isOpen,
  onClose,
  question,
  onQuestionUpdate,
  onSendMessage,
  isLoading = false
}) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      // Initialize with system message showing the question
      if (messages.length === 0) {
        setMessages([{
          id: 'system-1',
          role: 'system',
          content: 'Ich kann dir helfen, diese Frage zu verbessern. Sage mir, was du ändern möchtest.',
          timestamp: new Date()
        }]);
      }
    }
  }, [isOpen]);

  // Reset when question changes
  useEffect(() => {
    setMessages([{
      id: 'system-1',
      role: 'system',
      content: 'Ich kann dir helfen, diese Frage zu verbessern. Sage mir, was du ändern möchtest.',
      timestamp: new Date()
    }]);
  }, [question?.question]);

  // Quick action suggestions
  const quickActions = [
    { label: '🔥 Schwieriger machen', prompt: 'Mache diese Frage schwieriger und füge komplexere Antwortoptionen hinzu.' },
    { label: '✨ Vereinfachen', prompt: 'Vereinfache diese Frage und mache sie verständlicher für Anfänger.' },
    { label: '💻 Code hinzufügen', prompt: 'Füge ein passendes Code-Beispiel zu dieser Frage hinzu.' },
    { label: '🎯 Mehr Optionen', prompt: 'Füge eine 5. Antwortoption hinzu, die auch plausibel klingt.' },
    { label: '📝 Erklärung', prompt: 'Füge eine kurze Erklärung hinzu, warum die richtige Antwort korrekt ist.' },
    { label: '🔄 Variante', prompt: 'Erstelle eine Variante dieser Frage mit ähnlichem Schwierigkeitsgrad aber anderem Fokus.' }
  ];

  // Handle sending a message
  const handleSend = useCallback(async (messageText = inputValue) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: messageText.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');

    try {
      // Call parent's send handler with context
      const response = await onSendMessage({
        question,
        userMessage: messageText.trim(),
        conversationHistory: messages
      });

      if (response) {
        const assistantMessage = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: response.message,
          updatedQuestion: response.updatedQuestion,
          timestamp: new Date()
        };

        setMessages(prev => [...prev, assistantMessage]);

        // If we got an updated question, show apply button
        if (response.updatedQuestion) {
          // Auto-apply if the response contains a valid question
        }
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        role: 'error',
        content: `Fehler: ${error.message}`,
        timestamp: new Date()
      }]);
    }
  }, [inputValue, isLoading, question, messages, onSendMessage]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  // Apply an updated question from assistant
  const handleApplyQuestion = useCallback((updatedQuestion) => {
    onQuestionUpdate(updatedQuestion);
    setMessages(prev => [...prev, {
      id: `system-${Date.now()}`,
      role: 'system',
      content: '✅ Änderungen wurden übernommen.',
      timestamp: new Date()
    }]);
  }, [onQuestionUpdate]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Frage verfeinern
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Chat mit dem LLM um diese Frage zu verbessern
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg theme-transition"
          >
            ✕
          </button>
        </div>

        {/* Current Question Preview */}
        {question && (
          <div className="px-6 py-3 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Aktuelle Frage:
            </div>
            <p className="text-sm text-gray-800 dark:text-gray-200 line-clamp-2">
              {question.question}
            </p>
            <div className="flex flex-wrap gap-1 mt-2">
              {question.options?.map((opt, i) => (
                <span
                  key={i}
                  className={`text-xs px-2 py-0.5 rounded ${
                    question.correctAnswerIndices?.includes(i)
                      ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {String.fromCharCode(65 + i)}) {opt.slice(0, 30)}{opt.length > 30 ? '...' : ''}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
            Schnellaktionen:
          </div>
          <div className="flex flex-wrap gap-2">
            {quickActions.map((action, i) => (
              <button
                key={i}
                onClick={() => handleSend(action.prompt)}
                disabled={isLoading}
                className="text-xs px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 theme-transition disabled:opacity-50"
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                  message.role === 'user'
                    ? 'bg-primary-600 text-white rounded-br-md'
                    : message.role === 'error'
                    ? 'bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300 rounded-bl-md'
                    : message.role === 'system'
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-sm italic'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-bl-md'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                
                {/* Updated question preview */}
                {message.updatedQuestion && (
                  <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Vorgeschlagene Änderung:
                    </div>
                    <p className="text-sm text-gray-800 dark:text-gray-200 mb-2">
                      {message.updatedQuestion.question}
                    </p>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {message.updatedQuestion.options?.map((opt, i) => (
                        <span
                          key={i}
                          className={`text-xs px-2 py-0.5 rounded ${
                            message.updatedQuestion.correctAnswerIndices?.includes(i)
                              ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                          }`}
                        >
                          {String.fromCharCode(65 + i)}) {opt.slice(0, 25)}{opt.length > 25 ? '...' : ''}
                        </span>
                      ))}
                    </div>
                    <button
                      onClick={() => handleApplyQuestion(message.updatedQuestion)}
                      className="w-full py-2 bg-success-600 text-white text-sm font-medium rounded-lg hover:bg-success-700 theme-transition"
                    >
                      ✅ Änderungen übernehmen
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {/* Loading indicator */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-xs text-gray-500">Denkt nach...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30">
          <div className="flex gap-3">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Beschreibe, wie die Frage geändert werden soll..."
              rows={2}
              className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-xl resize-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
              disabled={isLoading}
            />
            <button
              onClick={() => handleSend()}
              disabled={!inputValue.trim() || isLoading}
              className="px-6 py-2 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 theme-transition disabled:opacity-50 disabled:cursor-not-allowed self-end"
            >
              {isLoading ? '...' : 'Senden'}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Enter zum Senden • Shift+Enter für neue Zeile
          </p>
        </div>
      </div>
    </div>
  );
}

RefinementChat.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  question: PropTypes.shape({
    question: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.string),
    correctAnswerIndices: PropTypes.arrayOf(PropTypes.number),
    codeBlock: PropTypes.object,
    terminalOutput: PropTypes.string
  }),
  onQuestionUpdate: PropTypes.func.isRequired,
  onSendMessage: PropTypes.func.isRequired,
  isLoading: PropTypes.bool
};
